'use client'

/**
 * Shared global CSS for the Papr funnel (keyframes, focus rings, hover states, and the
 * responsive `.onb-invoice-grid` columns). Scoped under `.papr-funnel`. Used by both the
 * `/preview-3` phase machine and the live onboarding page so the components render
 * identically in either context.
 */

import React from 'react'

export const FUNNEL_CSS = `
@keyframes paySpin { to { transform: rotate(360deg); } }
@keyframes onbStepIn { from { transform: translateY(10px); } to { transform: translateY(0); } }
@keyframes onbFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }

.papr-funnel .onb-step { animation: onbStepIn .34s cubic-bezier(.22,1,.36,1); }
.papr-funnel .onb-float { animation: onbFloat 5s ease-in-out infinite; }

.papr-funnel .onb-invoice-grid { grid-template-columns: minmax(0,1fr) minmax(0,440px); }
@media (max-width: 1180px) { .papr-funnel .onb-invoice-grid { grid-template-columns: 1fr; } }

.papr-funnel .onb-navlink:hover { background: rgba(0,0,0,0.04); color: #0a0a0b; }
@media (max-width: 900px) {
  .papr-funnel .onb-hero { grid-template-columns: 1fr !important; gap: 40px !important; }
  .papr-funnel .onb-feat { grid-template-columns: 1fr !important; }
}

.papr-funnel .onb-inwrap:focus-within { border-color: #0a0a0b !important; box-shadow: 0 0 0 3px rgba(10,10,11,0.08); }
.papr-funnel .onb-in:focus { border-color: #0a0a0b !important; box-shadow: 0 0 0 3px rgba(10,10,11,0.08); }
.papr-funnel .onb-code:focus { border-color: #0a0a0b !important; box-shadow: 0 0 0 3px rgba(202,255,51,0.5); }
.papr-funnel input::placeholder { color: #b7b1a6; }

.papr-funnel .onb-primary:not(:disabled):hover { filter: brightness(0.96); }
.papr-funnel .onb-primary:not(:disabled):active { transform: translateY(1px); }
.papr-funnel .onb-ghost:hover { background: rgba(0,0,0,0.03); }
.papr-funnel .onb-card:hover { border-color: rgba(0,0,0,0.22); }
.papr-funnel .onb-chip:hover { border-color: rgba(0,0,0,0.28); }

@media (prefers-reduced-motion: reduce) {
  .papr-funnel .onb-step, .papr-funnel .onb-float { animation: none !important; }
}

/* Mobile: prevent any horizontal overflow (clip is sticky-safe, unlike overflow:hidden) */
.papr-funnel { overflow-x: clip; }
/* Landing nav: drop the center links on small screens so the CTAs stay on-screen */
@media (max-width: 720px) {
  .papr-funnel .papr-nav-links { display: none !important; }
}
/* Auth/onboarding split-screen: hide the dark brand panel + onboarding rail on small
   screens so the form/content gets the full width (the form is 400px / maxWidth 100%). */
@media (max-width: 860px) {
  .papr-funnel .papr-brand-panel { display: none !important; }
  .papr-funnel .papr-onb-rail { display: none !important; }
  .papr-funnel .papr-onb-content { padding-left: 20px !important; padding-right: 20px !important; }
  .papr-funnel .papr-onb-footer { padding-left: 20px !important; padding-right: 20px !important; }
}
/* Small phones: drop the onboarding top-bar progress track so "Save & exit" isn't cramped */
@media (max-width: 520px) {
  .papr-funnel .papr-onb-progress { display: none !important; }
}
`

/**
 * Injects the funnel stylesheet.
 * Uses dangerouslySetInnerHTML because FUNNEL_CSS is a static, hardcoded constant
 * (no user input — no XSS surface) AND because passing CSS as `<style>` children
 * causes a React hydration text-content mismatch (the `%`/`>` chars in the keyframes
 * and media queries reconcile differently SSR vs client). innerHTML avoids the diff.
 */
export function FunnelStyles() {
  return <style dangerouslySetInnerHTML={{ __html: FUNNEL_CSS }} />
}

/**
 * Full-viewport `.papr-funnel` scope with the stylesheet injected. Wrap any funnel
 * surface (landing/auth/onboarding) so its `.onb-*` classes resolve.
 */
export function FunnelFrame({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div className="papr-funnel" style={{ width: '100%', height: '100vh', position: 'relative', overflow: 'hidden', ...style }}>
      <FunnelStyles />
      {children}
    </div>
  )
}
